import React from 'react';
import xmlescape from 'xml-escape';
const { DOMParser } = global;


import StoryCheckerDetailHighlightMatch from '../components/StoryCheckerDetailHighlightMatch';

// it'll always be represented as
//   just the tag name
//   with no attributes
const TAG_NAME_INCLUDED = 'included';
const TAG_NAME_EXCLUDED_PROFILE = 'excluded-by-profile';
const TAG_NAME_EXCLUDED_ACCOUNT = 'excluded-by-account';

const TAG_NAMES = new Set([
  TAG_NAME_INCLUDED, TAG_NAME_EXCLUDED_PROFILE, TAG_NAME_EXCLUDED_ACCOUNT
]);


/**
 * @private
 * @param {Node} node a Highlight Match node
 * @param {Object} mutableProps
 * @returns {Object} `mutableProps`
 */
function _applyHighlightProp(node, mutableProps) {
  const { nodeName } = node;
  switch (nodeName) {
    case TAG_NAME_INCLUDED:
      mutableProps.included = true;
      break;
    case TAG_NAME_EXCLUDED_PROFILE:
      mutableProps.excludedByProfile = true;
      break;
    case TAG_NAME_EXCLUDED_ACCOUNT:
      mutableProps.excludedByAccount = true;
      break;
    default:
  }
  return mutableProps;
}

/**
 * @private
 * @param {Number} ancestryIndex the Highlight Match node's index within the Ancestry
 * @param {Array<Node>} highlightAncestry
 * @returns {Boolean}
 */
function _showTooltipPerAncestry(ancestryIndex, highlightAncestry) {
  const { textContent } = highlightAncestry[ancestryIndex];
  const child = highlightAncestry[ancestryIndex + 1];
  if (child === undefined) {
    // i am the youngest, and am responsible for the ToolTip
    return true;
  }

  // if my immediate child displays the same content that i do,
  //   my child's composite messaging will have me covered.
  //   otherwise, i've got my own thing to say
  return (child.textContent !== textContent);
}

/**
 * @private
 * @param {Number} ancestryIndex the Highlight Match node's index within the Ancestry
 * @param {Array<Node>} highlightAncestry
 * @param {Object} mutableProps
 * @returns {Object} `mutableProps`
 */
function _mutatePropsPerAncestry(ancestryIndex, highlightAncestry, mutableProps) {
  const { textContent } = highlightAncestry[ancestryIndex];
  highlightAncestry.forEach((ancestorNode, index) => {
    // i compose the results of myself and my parents,
    //   but not my children
    if (index > ancestryIndex) {
      return;
    }

    // i only care about parents which match my exact text
    if (ancestorNode.textContent !== textContent) {
      return;
    }

    _applyHighlightProp(ancestorNode, mutableProps);
  });

  return mutableProps;
}

/**
 * @private
 * @param {Node} highlightNode
 * @param {Object} mutableState
 * @returns {React.Element}
 */
function _convertNodeToReactElement(node, mutableState) {
  const { nodeName, nodeType, attributes, textContent } = node;

  const { highlightAncestry, defaultProps } = mutableState;
  const keyIndex = (mutableState.keyIndex += 1);
  const isElementNode = (nodeType === 1); // ELEMENT_NODE

  const isHighlightTag = (isElementNode && TAG_NAMES.has(nodeName));
  let ancestryIndex;
  if (isHighlightTag) {
    ancestryIndex = highlightAncestry.length; // 0-based
    highlightAncestry.push(node);
  }

  const reactChildren = [];
  if (node.hasChildNodes()) {
    // a depth-first traversal
    Array.from(node.childNodes).forEach((childNode) => {
      reactChildren.push( _convertNodeToReactElement(childNode, mutableState) );
    });
  }

  let componentNameOrClass;
  let props = {
    // "Each child in an array or iterator should have a unique "key" prop."
    key: `highlight-${ keyIndex }`,
  };
  if (isHighlightTag) {
    componentNameOrClass = StoryCheckerDetailHighlightMatch;
    props = {
      ...props,
      ...defaultProps,
      text: textContent,
      nestingDepth: ancestryIndex,
      showTooltip: _showTooltipPerAncestry(ancestryIndex, highlightAncestry),
    };

    _mutatePropsPerAncestry(ancestryIndex, highlightAncestry, props);

    if (ancestryIndex === 0) {
      // we started the whole thing, so we clean it up;
      //   preserving the stack to its full depth drives `showTooltip`
      highlightAncestry.length = 0;
    }
  }
  else {
    if (attributes) {
      Array.from(attributes).forEach((attribute) => {
        props[attribute.name] = attribute.value;
      });
    }

    if (isElementNode) {
      componentNameOrClass = nodeName;
    }
    else {
      // eg. TEXT_NODE (3)
      componentNameOrClass = 'span';
      reactChildren.push(textContent);
    }
  }

  const reactElement = React.createElement(componentNameOrClass, props, reactChildren);
  return reactElement;
}

const _checkIfMarkupCanBeParsed = (markup) => {
  const domParser = new DOMParser();
  const docElement = domParser.parseFromString(`<div>${markup}</div>`, 'text/xml').documentElement;
  const childNode = docElement.childNodes[0];
  return childNode.nodeName === 'parsererror';
};

const _htmlEncode = (stringToEncode) => {
  let encodedString = xmlescape(stringToEncode, '<>');

  if (_checkIfMarkupCanBeParsed(encodedString)) {
    encodedString = '<div> Matching keywords can\'t be highlighted for this story </div>';
  }
  return encodedString;
};

/**
 * @param {String} markup
 * @param {Object} highlightProps only appied to a Match Component
 * @returns {React.Element}
 */
export function transformMarkupToReactElement(markup, highlightProps) {
  const defaultProps = Object.freeze({
    ...highlightProps,
    included: false,
    excludedByProfile: false,
    excludedByAccount: false,
  });
  const mutableState = {
    keyIndex: 0,
    highlightAncestry: [],
    defaultProps,
  };
  const domParser = new DOMParser();
  const encodedMarkup = _htmlEncode(markup);
  const doc = domParser.parseFromString(`<div>${encodedMarkup}</div>`, 'text/xml');

  return _convertNodeToReactElement(doc.documentElement, mutableState);
}


/**
 * @private
 * @param {String} markup
 * @param {String} tagName
 * @returns {Number} the number of occurances of the `tagName` tag in the `markup`
 */
function _getTagCount(markup, tagName) {
  const tag = `<${ tagName }>`;
  let count = 0;

  let lastIndex = -1;
  while ((lastIndex = markup.indexOf(tag, lastIndex + 1)) !== -1) {
    count += 1;
  }

  return count;
}

/**
 * @param {String} markup
 * @returns {Object} `{ includedCount, excludedByProfileCount, excludedByAccountCount }`
 *   with counts of occurances in `markup`
 */
export function deriveTagCountsFromMarkup(markup) {
  if (!markup) {
    return {
      includedCount: 0,
      excludedByProfileCount: 0,
      excludedByAccountCount: 0,
    };
  }
  return {
    includedCount: _getTagCount(markup, TAG_NAME_INCLUDED),
    excludedByProfileCount: _getTagCount(markup, TAG_NAME_EXCLUDED_PROFILE),
    excludedByAccountCount: _getTagCount(markup, TAG_NAME_EXCLUDED_ACCOUNT),
  };
}
